import { useCallback, useEffect, useRef, useState } from "react";
import { X, Camera, ScanLine, Keyboard, Zap, ZapOff, Repeat, Search, RefreshCw } from "lucide-react";
import BdsLogo from "@/components/BdsLogo";
import { inr } from "@/lib/format";

const DEBOUNCE_MS = 1200;

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 1180;
    gain.gain.value = 0.05;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close();
    }, 90);
  } catch {
    /* audio not available */
  }
};

const buzz = () => {
  if (navigator.vibrate) navigator.vibrate(35);
};

/**
 * QR / barcode scanner. Rear camera with torch + continuous scan mode for fast mobile billing,
 * and a manual field that also serves USB / Bluetooth keyboard-emulated scanners.
 */
export const QrScanner = ({
  open,
  onClose,
  onResult,
  title = "Scan Product QR",
  continuous: continuousDefault = false,
  allowContinuous = true,
  cartCount = 0,
  cartTotal = 0,
  lastMessage = "",
  autoCamera = false,
}) => {
  const [manual, setManual] = useState("");
  const [camera, setCamera] = useState(false);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [continuous, setContinuous] = useState(continuousDefault);
  const scannerRef = useRef(null);
  const lastScan = useRef({ code: "", at: 0 });
  const inputRef = useRef(null);
  const continuousRef = useRef(continuousDefault);

  useEffect(() => {
    continuousRef.current = continuous;
  }, [continuous]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 120);
      if (autoCamera) setCamera(true);
    } else {
      setManual("");
      setError("");
      setDenied(false);
      setCamera(false);
      setTorchOn(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const stopCamera = useCallback(() => {
    const inst = scannerRef.current;
    scannerRef.current = null;
    if (!inst) return;
    try {
      const state = inst.getState?.();
      // 2 = SCANNING, 3 = PAUSED — only those states can be stopped.
      if (state === 2 || state === 3) {
        inst.stop().then(() => inst.clear?.()).catch(() => {});
      } else {
        inst.clear?.();
      }
    } catch {
      /* scanner already stopped */
    }
  }, []);

  const handle = useCallback(
    (code, fromCamera = false) => {
      const value = String(code || "").trim();
      if (!value) return;
      if (fromCamera) {
        const now = Date.now();
        if (lastScan.current.code === value && now - lastScan.current.at < DEBOUNCE_MS) return;
        lastScan.current = { code: value, at: now };
        beep();
        buzz();
      }
      if (!fromCamera || !continuousRef.current) {
        stopCamera();
        setCamera(false);
      }
      onResult(value);
      if (!fromCamera) setManual("");
      if (!continuousRef.current) onClose?.();
    },
    [onResult, onClose, stopCamera]
  );

  useEffect(() => {
    let active = true;
    if (!camera) return undefined;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!active) return;
        const inst = new Html5Qrcode("bds-qr-reader", { verbose: false });
        scannerRef.current = inst;
        const config = { fps: 14, qrbox: { width: 250, height: 250 }, aspectRatio: 1 };
        const sources = [
          { facingMode: { exact: "environment" } },
          { facingMode: "environment" },
          { facingMode: "user" },
        ];
        let started = false;
        let lastErr = null;
        for (const source of sources) {
          try {
            await inst.start(source, config, (text) => handle(text, true), () => {});
            started = true;
            break;
          } catch (err) {
            lastErr = err;
            if (/permission|denied|NotAllowed/i.test(String(err?.message || err))) break;
          }
        }
        if (!started) throw lastErr || new Error("Camera could not be started");
        setDenied(false);
        setError("");
        try {
          const caps = inst.getRunningTrackCapabilities?.();
          setTorchAvailable(!!caps && "torch" in caps);
        } catch {
          setTorchAvailable(false);
        }
      } catch (e) {
        const msg = String(e?.message || e || "");
        const isDenied = /permission|denied|NotAllowed/i.test(msg);
        setDenied(isDenied);
        setError(
          isDenied
            ? "Camera permission is required to scan product QR codes."
            : "Camera not available on this device. Use a USB/Bluetooth scanner or type the code."
        );
        stopCamera();
        setCamera(false);
      }
    })();
    return () => {
      active = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera]);

  const toggleTorch = async () => {
    const inst = scannerRef.current;
    if (!inst) return;
    try {
      await inst.applyVideoConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((t) => !t);
    } catch {
      setTorchAvailable(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-5 sm:rounded-2xl"
        data-testid="qr-scanner-modal"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <BdsLogo size={26} showText={false} />
          <h3 className="flex min-w-0 flex-1 items-center gap-2 font-outfit text-base font-semibold">
            <ScanLine className="h-5 w-5 shrink-0 text-indigo-600" /> <span className="truncate">{title}</span>
          </h3>
          <button data-testid="close-qr-scanner" onClick={onClose}>
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="mb-3 flex items-center justify-between gap-2 rounded-xl bg-indigo-50 px-3 py-2">
          <span data-testid="scanner-cart-count" className="text-xs font-semibold text-indigo-800">
            Cart: {cartCount} item{cartCount === 1 ? "" : "s"}
          </span>
          <span data-testid="scanner-cart-total" className="text-xs font-semibold tnum text-indigo-800">
            {inr(cartTotal)}
          </span>
        </div>

        {lastMessage && (
          <p data-testid="scanner-last-message" className="mb-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
            {lastMessage}
          </p>
        )}

        {camera && (
          <div className="relative mb-3 overflow-hidden rounded-2xl bg-slate-900">
            <div id="bds-qr-reader" className="w-full" />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-52 w-52 rounded-2xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(15,23,42,0.35)]" />
            </div>
            <div className="absolute right-2 top-2 flex gap-2">
              {torchAvailable && (
                <button
                  data-testid="qr-torch-btn"
                  onClick={toggleTorch}
                  className="rounded-full bg-white/90 p-2 text-slate-800"
                  title="Torch"
                >
                  {torchOn ? <ZapOff className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                </button>
              )}
              <button
                data-testid="qr-stop-camera-btn"
                onClick={() => {
                  stopCamera();
                  setCamera(false);
                }}
                className="rounded-full bg-white/90 p-2 text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {!camera && (
          <button
            data-testid="qr-use-camera"
            onClick={() => setCamera(true)}
            className="mb-3 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 text-base font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            <Camera className="h-5 w-5" /> Open Camera &amp; Scan
          </button>
        )}

        {allowContinuous && (
          <label className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
            <span className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Repeat className="h-4 w-4 text-indigo-600" /> Continuous Scan (keep scanning, quantity +1 on repeat)
            </span>
            <input
              data-testid="qr-continuous-toggle"
              type="checkbox"
              checked={continuous}
              onChange={(e) => setContinuous(e.target.checked)}
              className="h-4 w-4"
            />
          </label>
        )}

        <div className="relative">
          <Keyboard className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <input
            ref={inputRef}
            data-testid="qr-manual-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handle(manual);
            }}
            placeholder="Scan with USB / Bluetooth scanner or type code"
            className="h-12 w-full rounded-xl border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-indigo-500"
          />
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Accepts QR value (BDS:PRODUCT:…), Product ID, barcode, SKU or STK code.
        </p>

        <button
          data-testid="qr-submit-code"
          onClick={() => handle(manual)}
          className="mt-3 h-11 w-full rounded-xl border border-slate-200 text-sm font-semibold text-slate-700"
        >
          Find Product
        </button>

        {error && (
          <div data-testid="qr-camera-error" className="mt-3 rounded-xl bg-amber-50 px-3 py-3 text-xs text-amber-800">
            <p className="font-semibold">{error}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                data-testid="qr-try-again-btn"
                onClick={() => {
                  setError("");
                  setDenied(false);
                  setCamera(true);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-800"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Try Again
              </button>
              <button
                data-testid="qr-enter-code-btn"
                onClick={() => {
                  setError("");
                  inputRef.current?.focus();
                }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-800"
              >
                <Keyboard className="h-3.5 w-3.5" /> Enter Product Code
              </button>
              <button
                data-testid="qr-search-product-btn"
                onClick={() => {
                  setError("");
                  onClose();
                }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-800"
              >
                <Search className="h-3.5 w-3.5" /> Search Product
              </button>
            </div>
            {denied && (
              <p className="mt-2 text-[11px]">
                Allow camera access from the padlock / site settings icon in your browser address bar, then tap
                Try Again.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QrScanner;
